/**
 * Générateur — Prévoir la valeur d'une expression informatique avant son
 * exécution.
 * Attendu ID : 1000 | Niveau : 5ème | Domaine : La pensée informatique
 *
 * Libellé officiel (spec 1000, 2026-09-21) : « Prévoir la valeur d'une
 * expression informatique avant son exécution. »
 *
 * Plan de cartes (mise en conformité spécification, 2026-09-22) :
 *
 *   Carte « valeur » — La valeur d'une expression. Une expression par blocs
 *   emboîtés, affichée en image, jamais exécutée ; l'élève prévoit sa valeur.
 *   Deux formats : QCM (les propositions sont des VALEURS, les distracteurs
 *   étant les valeurs que produisent les six erreurs visées) et saisie (un
 *   champ numérique, le type de réponse prescrit par la spec).
 *
 *   Carte « serie » — Une série de prévisions. Le tableau à deux colonnes de
 *   la spec : cinq ou six expressions en blocs, la première faite en exemple,
 *   une valeur à prévoir par ligne (texte à trous en saisie). Aux niveaux 1
 *   et 2, les deux formes d'emboîtement à deux opérations sont balayées ; aux
 *   niveaux 3 et 4, les quatre formes à trois opérations le sont dans la même
 *   série. Deux expressions d'une même série n'ont jamais la même valeur.
 *
 *   Cartes « predire », « batterie », « cadrage » (version précédente) :
 *   SUPPRIMÉES. Elles reposaient toutes les trois sur un bloc à VARIABLE
 *   (« ma variable », « réponse ») et sur une valeur d'entrée, que la spec
 *   exclut sans réserve (« Aucune variable : l'expression ne porte que des
 *   nombres »). Deux de leurs formats (QCM « quel calcul sera effectué »,
 *   QCM « quelle formule mathématique ce bloc calcule-t-il ») demandaient en
 *   outre la traduction de l'expression en écriture mathématique, que
 *   l'ancrage_programme renvoie explicitement à un autre objectif (998).
 *
 * Spécification appliquée :
 *
 *   Bornes : 2 ou 3 opérations ; opérandes entiers de 2 à 10, deux à deux
 *   distincts dans une même expression ; opérateurs + − × ÷ ; 6 formes
 *   d'emboîtement (2 à deux opérations, 4 à trois opérations) ; 5 ou 6
 *   expressions par série ; valeurs finales deux à deux distinctes dans une
 *   série.
 *   Exclusions : aucune variable, aucune structure de contrôle, aucun
 *   opérande valant 0 ou 1, aucun opérande répété, aucune valeur
 *   intermédiaire négative, nulle ou non entière aux niveaux 1 à 3, aucune
 *   exécution accessible (l'énoncé le dit, l'app n'exécute rien).
 *
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code pour le QCM,
 *   _meta.params.pieges pour les cartes en saisie) :
 *     1. lire les blocs de gauche à droite en ignorant l'emboîtement
 *                                             → lecture_gauche_droite
 *     2. inverser les opérandes d'une soustraction ou d'une division
 *                                             → operandes_inverses
 *     3. appliquer les priorités de l'écriture mathématique à des blocs
 *                                             → priorites_mathematiques
 *     4. commencer par le bloc le plus extérieur, l'intérieur en dernier
 *                                             → emboitement_pris_a_l_envers
 *     5. se tromper de niveau d'emboîtement entre deux blocs voisins
 *                                             → mauvais_niveau_d_emboitement
 *     6. refuser une valeur intermédiaire anormale et refaire dans un autre
 *        ordre pour tomber rond              → valeur_intermediaire_refusee
 *
 *   Graduation → formats :
 *     niveau 1 (2 opérations, + et ×)                  : valeur_texte, serie
 *     niveau 2 (2 opérations, − et ÷ en jeu)           : les trois formats
 *     niveau 3 (3 opérations, 4 formes balayées)       : les trois formats
 *     niveau 4 (3 opérations, intermédiaire anormale)  : les trois formats
 *
 *   Non tranché (voir audit/5eme/1000.md) : la spec prescrit un champ
 *   numérique par expression et l'exécution offerte APRÈS validation — l'app
 *   n'exécute aucun programme, la confrontation n'est pas rendue ; le QCM est
 *   conservé au titre de la règle d'équipe (un QCM par attendu) et ne sert
 *   que des valeurs ; le niveau 1 n'a pas de QCM, faute de trois valeurs
 *   fausses distinctes (sur + et ×, la lecture à plat, les priorités et
 *   l'emboîtement inversé donnent la même valeur) ; la valeur finale est
 *   toujours un entier strictement positif, à tous les niveaux.
 */

import { randomChoice, randInt, shuffle, makeQcm, makeSaisie, makeTexteTrou } from '../../../utils';
import { imgElement, COULEURS_BLOCS, MOINS } from './svgScratch';

const ATTENDU_ID = 1000;

const L_VALEUR = "La valeur d'une expression";
const L_SERIE = 'Une série de prévisions';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Bornes de la spécification ──────────────────────────────

const OPERANDE_MIN = 2, OPERANDE_MAX = 10;        // operande_de_l_expression
const SERIE_MIN = 5, SERIE_MAX = 6;               // nombre_d_expressions_par_exercice (4..6)

// ─── Arithmétique exacte (rationnels) ────────────────────────
// Les valeurs intermédiaires sont contrôlées à la génération : un calcul en
// flottant ferait passer 0,1 + 0,2 pour non entier et laisserait entrer des
// divisions à développement infini.

const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));
const rat = (n, d = 1) => {
    const signe = d < 0 ? -1 : 1;
    const g = pgcd(Math.abs(n), Math.abs(d)) || 1;
    return { n: (signe * n) / g, d: Math.abs(d) / g };
};
const plus = (a, b) => rat(a.n * b.d + b.n * a.d, a.d * b.d);
const moins = (a, b) => rat(a.n * b.d - b.n * a.d, a.d * b.d);
const fois = (a, b) => rat(a.n * b.n, a.d * b.d);
const divise = (a, b) => (b.n === 0 ? null : rat(a.n * b.d, a.d * b.n));
const appliquer = (op, a, b) => {
    if (!a || !b) return null;
    if (op === '+') return plus(a, b);
    if (op === MOINS) return moins(a, b);
    if (op === '×') return fois(a, b);
    return divise(a, b);
};

const estEntier = (r) => r.d === 1;
const estAffichable = (r) => 100 % r.d === 0;     // deux décimales au plus, exactes
const nombre = (r) => r.n / r.d;

/** Écriture française exacte d'un rationnel (jamais d'arrondi). */
const fmtNb = (r) => {
    const signe = r.n < 0 ? MOINS : '';
    const abs = Math.abs(r.n);
    if (r.d === 1) return `${signe}${abs}`;
    const centiemes = (abs * 100) / r.d;
    const entiere = Math.floor(centiemes / 100);
    const decimales = String(centiemes % 100).padStart(2, '0').replace(/0$/, '');
    return `${signe}${entiere},${decimales}`;
};

// ─── Expressions : arbres de nombres, jamais de variable ─────

const OPERATEURS = ['+', MOINS, '×', '/'];
const nd = (op, a, b) => ({ op, a, b });

const evaluer = (n) => {
    if (typeof n === 'number') return rat(n);
    return appliquer(n.op, evaluer(n.a), evaluer(n.b));
};

/** Valeurs des blocs emboîtés (tous les nœuds sauf la racine). */
const valeursIntermediaires = (racine) => {
    const out = [];
    const marcher = (x, estRacine) => {
        if (typeof x === 'number') return;
        marcher(x.a, false);
        marcher(x.b, false);
        if (!estRacine) out.push(evaluer(x));
    };
    marcher(racine, true);
    return out;
};

const cloner = (n) => (typeof n === 'number' ? n : nd(n.op, cloner(n.a), cloner(n.b)));
const noeuds = (n, acc = []) => {
    if (typeof n === 'number') return acc;
    acc.push(n);
    noeuds(n.a, acc);
    noeuds(n.b, acc);
    return acc;
};
/** Copie de l'arbre dont le k-ième nœud (parcours préfixe) a ses opérandes échangés. */
const echangerOperandes = (ast, k) => {
    const c = cloner(ast);
    const cible = noeuds(c)[k];
    const g = cible.a;
    cible.a = cible.b;
    cible.b = g;
    return c;
};
/** Profondeur de chaque opérande, dans l'ordre d'affichage (gauche à droite). */
const profondeurs = (n, p = 0, acc = []) => {
    if (typeof n === 'number') { acc.push(p); return acc; }
    profondeurs(n.a, p + 1, acc);
    profondeurs(n.b, p + 1, acc);
    return acc;
};

/**
 * Les six formes d'emboîtement de la spec, décrites par la suite affichée
 * x0 o0 x1 o1 x2 (o2 x3) : deux formes à deux opérations, quatre formes à
 * trois opérations (deux niveaux d'emboîtement). La forme équilibrée
 * (x0 o0 x1) o1 (x2 o2 x3), qui ne compte qu'un niveau, n'y est pas.
 */
const FORMES = {
    deux_gauche: {
        nbOps: 2, miroir: 'deux_droite',
        build: (x, o) => nd(o[1], nd(o[0], x[0], x[1]), x[2]),
    },
    deux_droite: {
        nbOps: 2, miroir: 'deux_gauche',
        build: (x, o) => nd(o[0], x[0], nd(o[1], x[1], x[2])),
    },
    trois_gauche_gauche: {
        nbOps: 3, miroir: 'trois_droite_droite',
        build: (x, o) => nd(o[2], nd(o[1], nd(o[0], x[0], x[1]), x[2]), x[3]),
    },
    trois_centre_gauche: {
        nbOps: 3, miroir: 'trois_centre_droite',
        build: (x, o) => nd(o[2], nd(o[0], x[0], nd(o[1], x[1], x[2])), x[3]),
    },
    trois_centre_droite: {
        nbOps: 3, miroir: 'trois_centre_gauche',
        build: (x, o) => nd(o[0], x[0], nd(o[2], nd(o[1], x[1], x[2]), x[3])),
    },
    trois_droite_droite: {
        nbOps: 3, miroir: 'trois_gauche_gauche',
        build: (x, o) => nd(o[0], x[0], nd(o[1], x[1], nd(o[2], x[2], x[3]))),
    },
};

export const FORMES_DE_NIVEAU = {
    1: ['deux_gauche', 'deux_droite'],
    2: ['deux_gauche', 'deux_droite'],
    3: ['trois_gauche_gauche', 'trois_centre_gauche', 'trois_centre_droite', 'trois_droite_droite'],
    4: ['trois_gauche_gauche', 'trois_centre_gauche', 'trois_centre_droite', 'trois_droite_droite'],
};

// ─── Erreurs visées : les valeurs fausses qu'elles produisent ─

/** Lecture à plat, de gauche à droite, sans tenir compte de l'emboîtement. */
const valeurAPlat = (x, o) => {
    let acc = rat(x[0]);
    for (let i = 0; i < o.length; i++) acc = appliquer(o[i], acc, rat(x[i + 1]));
    return acc;
};

/** Lecture à plat AVEC les priorités de l'écriture mathématique. */
const valeurAvecPriorites = (x, o) => {
    const vals = x.map((v) => rat(v));
    const ops = [...o];
    for (let i = 0; i < ops.length;) {
        if (ops[i] === '×' || ops[i] === '/') {
            const r = appliquer(ops[i], vals[i], vals[i + 1]);
            if (!r) return null;
            vals.splice(i, 2, r);
            ops.splice(i, 1);
        } else i++;
    }
    let acc = vals[0];
    for (let i = 0; i < ops.length; i++) acc = appliquer(ops[i], acc, vals[i + 1]);
    return acc;
};

/** Une valeur de bloc est « normale » si elle est un entier strictement positif. */
const estAnormale = (r) => r.n <= 0 || !estEntier(r);

/**
 * Les valeurs fausses que l'expression tirée provoque, chacune sous son
 * mot-clé. L'ordre est mélangé : quand deux erreurs donnent la même valeur
 * (fréquent sur + et ×), le mot-clé retenu varie d'un tirage à l'autre au
 * lieu d'être toujours le même.
 */
const piegesDe = (t) => {
    const { x, o, forme, ast, valeur } = t;
    const bruts = [
        { code: 'lecture_gauche_droite', r: valeurAPlat(x, o) },
        { code: 'priorites_mathematiques', r: valeurAvecPriorites(x, o) },
        { code: 'emboitement_pris_a_l_envers', r: evaluer(FORMES[FORMES[forme].miroir].build(x, o)) },
    ];

    // Niveau d'emboîtement confondu : deux opérandes voisins à l'affichage,
    // mais à des profondeurs différentes, sont échangés.
    const prof = profondeurs(ast);
    const i = prof.findIndex((p, k) => k < prof.length - 1 && p !== prof[k + 1]);
    if (i >= 0) {
        const y = [...x];
        y[i] = x[i + 1];
        y[i + 1] = x[i];
        bruts.push({ code: 'mauvais_niveau_d_emboitement', r: evaluer(FORMES[forme].build(y, o)) });
    }

    // Opérandes inversés dans une soustraction ou une division : sur un bloc
    // dont la valeur est normale (l'erreur 2), sur le bloc dont la valeur est
    // anormale (l'erreur 6, refaire le calcul pour tomber rond).
    noeuds(ast).forEach((n, k) => {
        if (n.op !== MOINS && n.op !== '/') return;
        const v = evaluer(n);
        if (!v) return;
        const code = estAnormale(v) ? 'valeur_intermediaire_refusee' : 'operandes_inverses';
        bruts.push({ code, r: evaluer(echangerOperandes(ast, k)) });
    });

    const vus = new Set([nombre(valeur)]);
    const out = [];
    shuffle(bruts).forEach(({ code, r }) => {
        if (!r || !estAffichable(r) || vus.has(nombre(r))) return;
        vus.add(nombre(r));
        out.push({ code, r });
    });
    return out;
};

// ─── Tirage d'une expression conforme au niveau ──────────────

const operateursAdmis = (niveau) => (niveau === 1 ? ['+', '×'] : OPERATEURS);

/**
 * Contrôle complet d'une expression : bornes, exclusions, contraintes du
 * niveau. Rend le tirage enrichi, ou null si l'expression est refusée.
 */
const controler = (niveau, forme, x, o) => {
    if (new Set(x).size !== x.length) return null;                     // opérandes deux à deux distincts
    if (x.some((v) => v < OPERANDE_MIN || v > OPERANDE_MAX)) return null;
    if (niveau === 2 && !o.some((op) => op === MOINS || op === '/')) return null;
    const ast = FORMES[forme].build(x, o);
    const valeur = evaluer(ast);
    if (!valeur || !estEntier(valeur) || valeur.n <= 0) return null;   // valeur finale entière et positive
    const inter = valeursIntermediaires(ast);
    if (inter.some((v) => v === null)) return null;
    if (inter.some((v) => v.n === 0)) return null;                     // aucune valeur intermédiaire nulle
    if (niveau <= 3 && inter.some((v) => estAnormale(v))) return null;
    if (niveau === 4) {
        if (inter.some((v) => !estAffichable(v))) return null;
        if (!inter.some((v) => estAnormale(v))) return null;           // le niveau 4 EST la valeur anormale
    }
    const t = { niveau, forme, x, o, ast, valeur };
    const pieges = piegesDe(t);
    if (pieges.length === 0) return null;                              // l'emboîtement doit changer la valeur
    return { ...t, inter, pieges };
};

/** Tuples d'opérandes balayés par le repli déterministe (tous distincts). */
const tuplesDeterministes = (n) => {
    const out = [];
    [1, 2, 4].forEach((pas) => {
        for (let s = 0; s < 9; s++) {
            out.push(Array.from({ length: n }, (_, k) => OPERANDE_MIN + ((s + k * pas) % 9)));
        }
    });
    return out;
};

/** Toutes les suites d'opérateurs de longueur n, dans un ordre fixe. */
const suitesOperateurs = (n, admis) => {
    let out = [[]];
    for (let i = 0; i < n; i++) out = out.flatMap((s) => admis.map((op) => [...s, op]));
    return out;
};

const CACHE_REPLI = new Map();
/** Balayage déterministe (memoïsé) : jusqu'à 40 expressions conformes. */
const repliDeterministe = (niveau, forme, minPieges) => {
    const cle = `${niveau}|${forme}|${minPieges}`;
    if (CACHE_REPLI.has(cle)) return CACHE_REPLI.get(cle);
    const nbOps = FORMES[forme].nbOps;
    const out = [];
    const tuples = tuplesDeterministes(nbOps + 1);
    const suites = suitesOperateurs(nbOps, operateursAdmis(niveau));
    for (let i = 0; i < tuples.length && out.length < 40; i++) {
        for (let j = 0; j < suites.length && out.length < 40; j++) {
            const t = controler(niveau, forme, tuples[i], suites[j]);
            if (t && t.pieges.length >= minPieges) out.push(t);
        }
    }
    CACHE_REPLI.set(cle, out);
    return out;
};

/** Dernier recours, jamais atteint en pratique : que des additions. */
const expressionDeSecours = (niveau, forme) => {
    const nbOps = FORMES[forme].nbOps;
    const x = [2, 3, 4, 5].slice(0, nbOps + 1);
    const o = Array.from({ length: nbOps }, () => '+');
    const ast = FORMES[forme].build(x, o);
    return {
        niveau, forme, x, o, ast, valeur: evaluer(ast),
        inter: valeursIntermediaires(ast), pieges: [],
    };
};

/**
 * Tire une expression du niveau et de la forme demandés : boucle bornée,
 * puis balayage déterministe, puis expression de secours. Jamais de throw :
 * un tirage impossible ne doit pas casser l'exerciseur.
 */
const tirerExpression = (niveau, forme, { minPieges = 1, interdits = [] } = {}) => {
    const nbOps = FORMES[forme].nbOps;
    const admis = operateursAdmis(niveau);
    const exclu = (t) => interdits.includes(nombre(t.valeur));
    for (let i = 0; i < 120; i++) {
        const x = shuffle(Array.from({ length: 9 }, (_, k) => OPERANDE_MIN + k)).slice(0, nbOps + 1);
        const o = Array.from({ length: nbOps }, () => randomChoice(admis));
        const t = controler(niveau, forme, x, o);
        if (t && t.pieges.length >= minPieges && !exclu(t)) return t;
    }
    const candidats = repliDeterministe(niveau, forme, minPieges).filter((t) => !exclu(t));
    if (candidats.length) return candidats[0];
    return expressionDeSecours(niveau, forme);
};

// ─── Rendu SVG local (mêmes cotes et même palette que le dossier) ─
// Un seul <svg> par image, jamais d'imbrication : des <g transform>.

const FS = 13;
const EH = 24, PAD = 7, W_OP = 18;
const largeurTexte = (t) => Math.ceil(String(t).length * 6.9) + 4;
const rect = (x, y, w, h, fill, rx = 6) =>
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${rx}" fill="${fill}" stroke="#0f172a" stroke-opacity="0.25" stroke-width="1"/>`;
const txt = (x, y, s, o = {}) =>
    `<text x="${x}" y="${y}" font-size="${o.size || FS}" fill="${o.fill || '#fff'}"${o.milieu ? ' text-anchor="middle"' : ''}${o.gras ? ' font-weight="bold"' : ''} font-family="Arial">${s}</text>`;

// Un bloc emboîté est plus clair que celui qui le contient : c'est ce que
// l'élève doit lire, et un aplat unique rend les niveaux indiscernables.
const VERTS = [COULEURS_BLOCS.op, '#46a846', '#56b856'];

const construireExpr = (n, profondeur = 0) => {
    if (typeof n === 'number') {
        const s = String(n);
        const w = largeurTexte(s) + 14;
        return {
            w, h: EH,
            frag: (x, y) => rect(x, y, w, EH, '#ffffff', EH / 2)
                + txt(x + w / 2, y + EH / 2 + 5, s, { fill: '#0f172a', milieu: true }),
        };
    }
    const a = construireExpr(n.a, profondeur + 1);
    const b = construireExpr(n.b, profondeur + 1);
    const h = Math.max(a.h, b.h) + 2 * PAD;
    const w = PAD + a.w + W_OP + b.w + PAD;
    const fond = VERTS[Math.min(profondeur, VERTS.length - 1)];
    return {
        w, h,
        frag: (x, y) => rect(x, y, w, h, fond, h / 2)
            + a.frag(x + PAD, y + (h - a.h) / 2)
            + txt(x + PAD + a.w + W_OP / 2, y + h / 2 + 5, n.op, { milieu: true, gras: true, size: FS + 1 })
            + b.frag(x + PAD + a.w + W_OP, y + (h - b.h) / 2),
    };
};

const enveloppe = (frag, w, h) =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w + 12} ${h + 12}" width="${w + 12}" height="${h + 12}" font-family="Arial">`
    + `<rect x="0" y="0" width="${w + 12}" height="${h + 12}" fill="#eef2f7" rx="8"/><g transform="translate(6,6)">${frag}</g></svg>`;

/** Une expression par blocs emboîtés, seule. */
const svgExpression = (ast) => {
    const e = construireExpr(ast);
    return enveloppe(e.frag(0, 0), e.w, e.h);
};

/**
 * Le tableau à deux colonnes de la spec : l'expression en blocs, la valeur à
 * prévoir. La première ligne est complétée, elle sert d'exemple.
 */
const svgTableau = (lignes) => {
    const items = lignes.map((l) => ({ ...l, e: construireExpr(l.ast) }));
    const wEtiq = 34;
    const wExpr = Math.max(...items.map((i) => i.e.w)) + 20;
    const wVal = 112;
    const W = wEtiq + wExpr + wVal;
    const hEntete = 26;
    const hauteurs = items.map((i) => Math.max(34, i.e.h + 12));
    const H = hEntete + hauteurs.reduce((s, h) => s + h, 0);

    let frag = rect(0, 0, W, hEntete, '#dbe3ec', 0)
        + txt(wEtiq + 10, 18, 'Expression', { fill: '#1e293b', gras: true })
        + txt(wEtiq + wExpr + wVal / 2, 18, 'Valeur prévue', { fill: '#1e293b', gras: true, milieu: true });
    let y = hEntete;
    items.forEach((i, k) => {
        const h = hauteurs[k];
        frag += rect(0, y, W, h, '#ffffff', 0)
            + txt(10, y + h / 2 + 5, i.etiquette, { fill: '#1e293b', gras: true })
            + i.e.frag(wEtiq + 10, y + (h - i.e.h) / 2)
            + txt(wEtiq + wExpr + wVal / 2, y + h / 2 + 5, i.valeur === null ? '?' : i.valeur,
                { fill: '#0f172a', milieu: true, gras: true });
        y += h;
    });
    frag += `<line x1="${wEtiq}" y1="0" x2="${wEtiq}" y2="${H}" stroke="#0f172a" stroke-opacity="0.25"/>`
        + `<line x1="${wEtiq + wExpr}" y1="0" x2="${wEtiq + wExpr}" y2="${H}" stroke="#0f172a" stroke-opacity="0.25"/>`;
    return enveloppe(frag, W, H);
};

// ─── Énoncés communs ─────────────────────────────────────────

const PAS_D_EXECUTION = "Ce programme n'est pas exécuté.";

/** Le niveau 4 annonce les valeurs intermédiaires anormales (spec, graduation). */
const annonce = (niveau, plusieurs = false) => (niveau !== 4 ? ''
    : `<p>Dans ${plusieurs ? 'ces expressions' : 'cette expression'}, la valeur d'un `
        + 'bloc emboîté peut être négative ou décimale.</p>');

const metaExpression = (t) => ({
    niveau: t.niveau,
    forme: t.forme,
    nb_operations: FORMES[t.forme].nbOps,
    operandes: t.x,
    operateurs: t.o,
    intermediaires: t.inter.map((r) => nombre(r)),
    valeur: nombre(t.valeur),
    pieges: t.pieges.map((p) => p.code),
});

// ═════════ Carte « valeur » — QCM et saisie ═════════

const genValeurQcm = () => {
    // Niveau 1 exclu : sur + et ×, la lecture à plat, les priorités et
    // l'emboîtement inversé donnent la même valeur — trois propositions
    // fausses distinctes n'existent pas toujours, et le nombre de
    // propositions doit rester constant.
    const niveau = randInt(2, 4);
    const forme = randomChoice(FORMES_DE_NIVEAU[niveau]);
    const t = tirerExpression(niveau, forme, { minPieges: 3 });
    const distracteurs = t.pieges.slice(0, 3);
    const bonne = fmtNb(t.valeur);
    const choix = [bonne, ...distracteurs.map((d) => fmtNb(d.r))];
    const texte = `<p><b>${PAS_D_EXECUTION} Quelle valeur cette expression `
        + 'prendra-t-elle ?</b></p>' + annonce(niveau);
    const payload = makeQcm(choix, bonne, texte,
        { ...opts(L_VALEUR), elements_additionnels: imgElement(svgExpression(t.ast)) });
    payload._meta.params = {
        ...metaExpression(t),
        distracteurs: distracteurs.map((d) => ({ code: d.code, valeur: nombre(d.r) })),
    };
    return payload;
};

const genValeurSaisie = () => {
    const niveau = randInt(1, 4);
    const forme = randomChoice(FORMES_DE_NIVEAU[niveau]);
    const t = tirerExpression(niveau, forme);
    const texte = `<p><b>${PAS_D_EXECUTION} Prévois la valeur que cette expression `
        + 'prendra.</b></p>' + annonce(niveau)
        + '<p>La valeur cherchée est un nombre entier.</p>';
    const payload = makeSaisie(fmtNb(t.valeur), texte,
        { ...opts(L_VALEUR), elements_additionnels: imgElement(svgExpression(t.ast)) });
    payload._meta.params = { ...metaExpression(t), erreurVisee: t.pieges[0] ? t.pieges[0].code : null };
    return payload;
};

// ═════════ Carte « serie » — le tableau à deux colonnes ═════════

const ETIQUETTES = ['(a)', '(b)', '(c)', '(d)', '(e)', '(f)'];

/** Formes de la série : toutes celles du niveau, l'exemple en plus. */
const formesDeLaSerie = (niveau, nbLignes) => {
    const dispo = FORMES_DE_NIVEAU[niveau];
    const aPrevoir = shuffle([...dispo]);
    while (aPrevoir.length < nbLignes - 1) aPrevoir.push(randomChoice(dispo));
    return [randomChoice(dispo), ...shuffle(aPrevoir.slice(0, nbLignes - 1))];
};

const genSerieTrous = () => {
    const niveau = randInt(1, 4);
    const nbLignes = randInt(SERIE_MIN, SERIE_MAX);
    const formes = formesDeLaSerie(niveau, nbLignes);
    const valeursVues = [];
    const tirages = formes.map((forme) => {
        const t = tirerExpression(niveau, forme, { interdits: valeursVues });
        valeursVues.push(nombre(t.valeur));
        return t;
    });

    const lignes = tirages.map((t, k) => ({
        etiquette: ETIQUETTES[k],
        ast: t.ast,
        valeur: k === 0 ? fmtNb(t.valeur) : null,
    }));
    const phrase = tirages.slice(1)
        .map((t, k) => `L'expression ${ETIQUETTES[k + 1]} prendra la valeur {{${fmtNb(t.valeur)}}}.`)
        .join(' ');
    const texte = `<p><b>${PAS_D_EXECUTION} Prévois la valeur de chaque expression.</b></p>`
        + annonce(niveau, true)
        + `<p>La ligne ${ETIQUETTES[0]} est faite en exemple. Chaque valeur cherchée `
        + 'est un nombre entier.</p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_SERIE), mode: 'saisie',
        elements_additionnels: imgElement(svgTableau(lignes), 'medium'),
    });
    payload._meta.params = {
        niveau,
        nb_expressions: nbLignes,
        formes,
        expressions: tirages.map((t) => metaExpression(t)),
        valeurs: valeursVues,
        pieges: [...new Set(tirages.flatMap((t) => t.pieges.map((p) => p.code)))],
    };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ───────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        "l'expression ne porte que des nombres : aucune variable, aucune structure de contrôle (exclusions de la spec)",
        "aucune exécution n'est accessible : l'énoncé le dit, l'app n'exécute aucun programme",
        'chaque distracteur est la valeur produite par une erreur visée, et porte son mot-clé',
    ],
    valeur_qcm_unique: 'trois valeurs fausses tirées parmi lecture_gauche_droite, priorites_mathematiques, '
        + 'emboitement_pris_a_l_envers, mauvais_niveau_d_emboitement, operandes_inverses, '
        + 'valeur_intermediaire_refusee ; toute valeur égale à la bonne réponse ou à une autre '
        + 'proposition est écartée, et le tirage est rejoué jusqu\'à en avoir trois distinctes',
    valeur_texte: "un champ numérique, le type de réponse prescrit ; l'erreur que l'expression "
        + 'provoque est nommée dans _meta.params.erreurVisee',
    serie_texte_trou_saisie: 'aucun distracteur (saisie) ; les valeurs des expressions d\'une même '
        + 'série sont deux à deux distinctes, sinon une prévision juste pourrait être portée au '
        + "crédit d'une autre expression",
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    valeur_qcm_unique: genValeurQcm,
    valeur_texte: genValeurSaisie,
    serie_texte_trou_saisie: genSerieTrous,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'valeur', label: L_VALEUR, formatsList: buildFormats('valeur') },
    { id: 'serie', label: L_SERIE, formatsList: buildFormats('serie') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
